import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { api, sessao } from "@/lib/api";
import type { Papel, TipoAprovacao, Usuario } from "@/types";

/** Resposta dos endpoints que abrem sessão (login, convite, redefinição). */
export interface SessaoAberta {
  accessToken: string;
  refreshToken: string;
  usuario: Usuario;
}

interface Contexto {
  usuario: Usuario | null;
  carregando: boolean;
  entrar: (email: string, senha: string) => Promise<void>;
  /** Adota a sessão devolvida por concluir cadastro ou redefinir senha. */
  abrirSessao: (sessao: SessaoAberta) => void;
  /** Atualiza o usuário exibido (ex.: depois de confirmar um novo e-mail). */
  atualizarUsuario: (usuario: Usuario) => void;
  sair: () => void;
  podeEditar: boolean;
  ehAdmin: boolean;
  /** Etapa que o usuário classifica; `null` para quem só envia e consulta. */
  tipoAprovacao: TipoAprovacao | null;
}

/** Espelho de `TIPO_POR_PAPEL` do backend. */
const TIPO_POR_PAPEL: Partial<Record<Papel, TipoAprovacao>> = {
  contabil: "contabil",
  tax: "tax",
  admin: "negocio",
};

const AuthContext = React.createContext<Contexto | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [usuario, setUsuario] = React.useState<Usuario | null>(null);
  const [carregando, setCarregando] = React.useState(true);
  const queryClient = useQueryClient();

  // Reidrata a sessão a partir do refresh token guardado (F5 não desloga).
  React.useEffect(() => {
    let ativo = true;
    (async () => {
      if (!sessao.refresh) {
        setCarregando(false);
        return;
      }
      try {
        const eu = await api<Usuario>("/auth/eu");
        if (ativo) setUsuario(eu);
      } catch {
        sessao.limpar();
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, []);

  // Se o refresh expirar no meio do uso, a UI volta para o login sozinha.
  React.useEffect(() => {
    const cancelar = sessao.aoMudar(() => {
      if (!sessao.access && !sessao.refresh) setUsuario(null);
    });
    return () => {
      cancelar();
    };
  }, []);

  const abrirSessao = React.useCallback(
    (resposta: SessaoAberta) => {
      queryClient.clear();
      sessao.definir(resposta.accessToken, resposta.refreshToken);
      setUsuario(resposta.usuario);
    },
    [queryClient],
  );

  const entrar = React.useCallback(
    async (email: string, senha: string) => {
      const resposta = await api<SessaoAberta>("/auth/login", {
        method: "POST",
        body: { email, senha },
      });
      abrirSessao(resposta);
    },
    [abrirSessao],
  );

  const sair = React.useCallback(() => {
    sessao.limpar();
    setUsuario(null);
    queryClient.clear();
  }, [queryClient]);

  const valor = React.useMemo<Contexto>(
    () => ({
      usuario,
      carregando,
      entrar,
      abrirSessao,
      atualizarUsuario: setUsuario,
      sair,
      podeEditar: usuario?.papel === "admin" || usuario?.papel === "editor",
      ehAdmin: usuario?.papel === "admin",
      tipoAprovacao: (usuario && TIPO_POR_PAPEL[usuario.papel]) ?? null,
    }),
    [usuario, carregando, entrar, abrirSessao, sair],
  );

  return <AuthContext.Provider value={valor}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const contexto = React.useContext(AuthContext);
  if (!contexto) throw new Error("useAuth precisa estar dentro de <AuthProvider>.");
  return contexto;
}
